/**
 * In-process fan-out of completed frame episodes. The perf debug panel's live
 * strip is the only consumer today; the RUM action itself goes through
 * `finishTracking` and never through this.
 */
import type { FrameSurface, FrameTrigger } from './surfaces';

export type FrameEpisodeResult = {
  surface: FrameSurface;
  trigger: FrameTrigger;
  frames: number;
  long_frames: number;
  frozen_frames: number;
  worst_gap_ms: number;
  tail_gap_ms: number;
  /** Share of the episode spent beyond a 60 Hz frame budget. Panel-independent. */
  jank_pct: number;
  /** `jank_pct` in frames: the overshoot expressed in 60 Hz frames. */
  dropped_frames_60: number;
  /** Snapped refresh rate the callbacks were delivered at; 0 when too few ran. */
  cadence_hz: number;
  capped: boolean;
  totalDurationMs: number;
  /** `Date.now()` at completion, for the panel's age column. */
  completedAt: number;
};

export type FrameEpisodeListener = (episode: FrameEpisodeResult) => void;

const listeners = new Set<FrameEpisodeListener>();

export function subscribeFrameEpisodes(listener: FrameEpisodeListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function publishFrameEpisode(episode: FrameEpisodeResult): void {
  for (const listener of listeners) {
    // A throwing panel must not take down the sampler that fed it.
    try {
      listener(episode);
    } catch {
      // Dev-only consumer; nothing to escalate to.
    }
  }
}

export function __resetFrameEpisodeListenersForTests(): void {
  listeners.clear();
}
